import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, LayoutDashboard } from 'lucide-react';
import { useAuthStatus } from '../hooks/useAuthStatus';
import { useReferralCta } from '../hooks/useReferralCta';
import { trackCtaClick } from '../utils/analytics';

//
// Sticky bottom CTA — spec sec. 6 (Functional Requirements — Sticky CTA):
// mobile sticky footer reading "Am I Qualified?", routing to /intake.
// Hidden inside the /intake funnel itself (it has its own footer) and on
// first paint until the hero has scrolled away — keeps the hero CTA primary.
// Also hidden on the /preventive-health waitlist pages (client 2026-10-10):
// there the only action is "Join Early Access", and mobile users were
// tapping this bar by mistake.
//
export default function StickyMobileCta() {
  const authed = useAuthStatus();
  const { ctaUrl } = useReferralCta();
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();

  const inIntake = pathname === '/intake' || pathname.startsWith('/intake/');
  const onWaitlist = pathname.startsWith('/preventive-health');

  useEffect(() => {
    function onScroll() { setVisible(window.scrollY > 600); }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (inIntake || onWaitlist) return null;

  function onClick(destination, label) {
    trackCtaClick({ page: pathname, button_label: label, destination });
  }

  return (
    <div
      className={[
        'lg:hidden fixed inset-x-0 bottom-0 z-40 px-4 pb-4 pt-3',
        'bg-gradient-to-t from-white via-white/95 to-white/0',
        'transition-all duration-300',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0 pointer-events-none',
      ].join(' ')}
      aria-hidden={!visible}
    >
      {authed ? (
        <a
          href={ctaUrl}
          onClick={() => onClick('portal_dashboard', 'Open my dashboard')}
          className="w-full inline-flex items-center justify-center gap-2 font-semibold rounded-full px-5 py-3.5 text-sm bg-brand-600 text-white shadow-card"
        >
          <LayoutDashboard size={16} /> Open my dashboard
        </a>
      ) : (
        <Link
          to="/intake"
          onClick={() => onClick('/intake', 'Start My Assessment')}
          className="w-full inline-flex items-center justify-center gap-2 font-semibold rounded-full px-5 py-3.5 text-sm bg-brand-600 text-white shadow-card"
        >
          Start My Assessment <ArrowRight size={16} />
        </Link>
      )}
    </div>
  );
}
